import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Receipt, CheckCircle2, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const eur = (v) => Number(v).toFixed(2).replace(".", ",");

// Ricevute già emesse nel mese corrente (set di azienda_id) + incassi Stripe del mese per azienda
export function useRicevuteMese(token, refreshKey) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!token) return;
    const ym = new Date().toISOString().slice(0, 7);
    const h = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/api/super-admin/ricevute`, h),
      axios.get(`${API}/api/super-admin/ricevute/incassi-mese`, h),
    ])
      .then(([r, inc]) => {
        const emesse = new Set(r.data.ricevute.filter((x) => (x.data || "").startsWith(ym)).map((x) => x.azienda_id));
        const incassi = Object.fromEntries((inc.data.aziende || []).map((a) => [a.azienda_id, a]));
        setState({ emesse, incassi });
      })
      .catch(() => setState({ emesse: new Set(), incassi: {} }));
  }, [token, refreshKey]);
  return state;
}

export function RicevuteDaEmettere({ companies, stato, onEmetti }) {
  if (!stato) return null;
  const { emesse, incassi } = stato;
  const now = new Date();
  const mese = `${MESI[now.getMonth()]} ${now.getFullYear()}`;
  const paganti = companies.filter((c) => c.payment_completed);
  const daEmettere = paganti.filter((c) => !emesse.has(c.id) && (incassi[c.id]?.pagamenti || []).length > 0);
  const inAttesa = paganti.filter((c) => !emesse.has(c.id) && (incassi[c.id]?.pagamenti || []).length === 0);
  const fatte = paganti.filter((c) => emesse.has(c.id));

  return (
    <Card className={`mt-8 ${daEmettere.length ? "border-amber-300 bg-amber-50/60" : "border-emerald-200 bg-emerald-50/50"}`} data-testid="ricevute-da-emettere-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Receipt className={`h-5 w-5 ${daEmettere.length ? "text-amber-600" : "text-emerald-600"}`} />
          Ricevute di {mese}: {daEmettere.length ? `${daEmettere.length} da emettere` : "nessuna da emettere"}
          <span className="text-sm font-normal text-slate-500 ml-1">({fatte.length} emesse su {paganti.length} aziende paganti)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">Si basa sui pagamenti realmente incassati su Stripe in questo mese: un'azienda compare "da emettere" solo dopo che ha pagato.</p>
        {daEmettere.length === 0 ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="ricevute-tutte-emesse">
            <CheckCircle2 className="h-4 w-4" /> Nessun pagamento incassato senza ricevuta.
          </p>
        ) : (
          <ul className="divide-y divide-amber-200" data-testid="ricevute-mancanti-list">
            {daEmettere.map((c) => (
              <li key={c.id} className="py-2 flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-900">{c.nome_azienda}</p>
                  <p className="text-xs text-slate-600">
                    {incassi[c.id].pagamenti.map((p) => `pagato il ${fmt(p.data)} — € ${eur(p.importo)}`).join(" · ")}
                  </p>
                </div>
                <Button size="sm" onClick={() => onEmetti(c)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`emetti-ricevuta-${c.id}`}>
                  <Receipt className="h-4 w-4 mr-1" /> Emetti ricevuta
                </Button>
              </li>
            ))}
          </ul>
        )}
        {inAttesa.length > 0 && (
          <div data-testid="ricevute-in-attesa">
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1"><Clock className="h-3.5 w-3.5" /> In attesa del pagamento di questo mese</p>
            <ul className="text-sm text-slate-600 space-y-0.5">
              {inAttesa.map((c) => (
                <li key={c.id}>
                  {c.nome_azienda}
                  {incassi[c.id]?.prossimo_rinnovo ? ` — rinnovo previsto il ${fmt(incassi[c.id].prossimo_rinnovo)}` : incassi[c.id]?.stripe_ok ? "" : " — nessun abbonamento Stripe collegato"}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
